import React from 'react';
import Icon from '@material-ui/core/Icon';
import Button from '@material-ui/core/Button';
import Fab from '@material-ui/core/Fab';
import AddIcon from '@material-ui/icons/Add';
import { withTranslation, WithTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { connect } from 'react-redux';
// Components
import App from '../../../components/app';
import EnhancedTable from '../../../components/table';
import TooltipButton from '../../../components/tooltip-button/tooltip-button';
import ErrorMessage from '../../../components/error-message';
// Modules
import { tableRequestPoints } from '../../../redux/reducers/constants';
import { ErrorPayload, selectors as errorSelectors } from '../../../redux/reducers/errors';
import { actions as vehicleActions, selectors as vehicleSelectors } from '../../../redux/reducers/vehicles';
import { VehicleType, VehicleTypeDetails } from '../../../redux/reducers/api-types';
// Ressources
import withGlobalStyles from '../../../utils/global-styles';
import UserUtils, { AccountRight, RightRank } from '../../../utils/userUtils';
import { paths } from '../../paths';
import { generateOptions } from '../../../utils/businessUtils';
import { FormatType, HeadCell, PickerOption } from '../../../local-types';
import { Cell, createCell } from '../../../components/table/table-cell-creator';

interface VehiclesTypesListProps extends WithTranslation {
    globalClasses: any;
    types: VehicleTypeDetails[];
    getVehicleTypesError: ErrorPayload;
    getVehicleTypes: () => void;
}
class VehiclesTypesListPage extends React.Component<VehiclesTypesListProps> {
    canWriteSettings: boolean = UserUtils.checkRights({
        name: AccountRight.COMPANY_SETTINGS,
        mode: RightRank.W,
    });

    componentDidMount() {
        this.props.getVehicleTypes();
    }

    formatCellMaxEligibilityAge = (row) => {
        const dg = row.dispatch_groups[0];
        const infos = ''; //dg?.dispatch_group?.name ? `(${dg?.dispatch_group.name})` : '';
        return `${dg?.max_eligibility_age ?? ''} ${infos}`;
    };

    formatCellType = (row: VehicleType) => {
        return `${row.type?.localized_name?.fr}`;
    };

    generateTypeOptions = (): PickerOption[] => {
        const { types } = this.props;
        return generateOptions(types, 'id', 'localized_name.fr');
    };

    headerBuilder = (): HeadCell[] => {
        const { t, globalClasses } = this.props;
        const headers: HeadCell[] = [
            createCell(t, Cell.ID, { sortable: true }),
            { id: 'brand.name', label: t('brand'), contains: false },
            { id: 'localized_string.fr', label: t('model'), contains: false },
            {
                id: 'type.localized_name',
                label: t('type'),
                contains: false,
                type: FormatType.Picker,
                pickerOptions: this.generateTypeOptions(),
                formatCell: this.formatCellType,
            },
            {
                id: 'max_age',
                label: t('max_age'),
                type: FormatType.Number,
                contains: false,
            },
            {
                id: 'dispatch_groups.max_eligibility_age',
                label: t('max_eligibility_age'),
                type: FormatType.Number,
                contains: false,
                formatCell: this.formatCellMaxEligibilityAge,
            },
        ];
        if (this.canWriteSettings) {
            headers.push({
                id: 'action',
                label: t('common:action'),
                filterable: false,
                sortable: false,
                noLink: true,
                formatCell: (row) => {
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <TooltipButton title={t('common:edit')}>
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="secondary"
                                    style={{ marginRight: 8, padding: 6 }}
                                    component={RouterLink}
                                    to={`${paths.vehiclesTypesAdd}?id=${row.id}`}
                                    className={globalClasses.btnAutoWidth}
                                >
                                    <Icon fontSize="small">edit</Icon>
                                </Button>
                            </TooltipButton>
                        </div>
                    );
                },
            });
        }
        return headers;
    };

    render() {
        const { t, globalClasses, getVehicleTypesError } = this.props;

        return (
            <App
                disableBreadcrumb
                header={{
                    title: t('types.title'),
                    subtitle: t('types.description'),
                }}
            >
                <ErrorMessage payload={getVehicleTypesError} />
                <EnhancedTable headers={this.headerBuilder()} type={tableRequestPoints.VEHICLES_TYPES} />
                <br />
                {this.canWriteSettings && (
                    <>
                        <br />
                        <Fab
                            className={globalClasses.fabBotRight}
                            component={RouterLink}
                            to={paths.vehiclesTypesAdd}
                            color="secondary"
                            aria-label={t('common:add')}
                        >
                            <AddIcon />
                        </Fab>
                    </>
                )}
            </App>
        );
    }
}

const VehiclesTypesListPageWithTranslation = withGlobalStyles(
    withTranslation(['vehicle', 'common'])(VehiclesTypesListPage),
);

const mapStateToProps = (state) => ({
    types: vehicleSelectors.types(state),
    getVehicleTypesError: errorSelectors.getVehicleTypesError(state),
});

const mapDispatchToProps = {
    getVehicleTypes: vehicleActions.getVehicleTypes,
};

export default connect(mapStateToProps, mapDispatchToProps)(VehiclesTypesListPageWithTranslation);
